import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import type { ButtonVariant } from "@/components/Button";
import { buildCopyAriaLabel, getCopyAnalyticsAttributes } from "@/lib/copyAriaLabel";
import { copyTextToClipboard } from "@/lib/copyToClipboard";
import Icon from "../Icon";
import { showSnackbar, showErrorSnackbar } from "@/lib/snackbarUtils";

interface CopyButtonProps {
  textToCopy: string;
  /** Field name for analytics-safe labeling (e.g. `Phone`, `PGA ID`). Never embed PII in this string. */
  copyFieldLabel?: string;
  /** Internal system identifier; only set when `copyFieldLabel` is an ID field (e.g. PGA ID value). */
  copyFieldId?: string;
  /** Full override for the accessible name when field/id props are not sufficient. */
  ariaLabel?: string;
  className?: string;
  children?: React.ReactNode;
  isMobile?: boolean;
  feedback?: "tooltip" | "snackbar" | "none";
  /** Snackbar text when `feedback` is `snackbar`. Defaults to `Copied`. */
  successMessage?: string;
  /** Material-style button variant for inline actions (e.g. side sheet headers). */
  variant?: ButtonVariant;
  /** When false, hides the copy icon. Defaults to false when `variant` and `children` are set. */
  showCopyIcon?: boolean;
  /** Copy icon placement relative to `children`. Defaults to `start`. */
  iconPosition?: "start" | "end";
  onCopied?: () => void;
  testId?: string;
}

function getCopyButtonClassName(variant?: ButtonVariant) {
  const base = "flex cursor-pointer items-center gap-1 border-0";

  switch (variant) {
    case "text":
      return classNames(
        base,
        "rounded-full px-3 py-2 text-label-large text-sys-brand-primary hover:bg-sys-brand-on-surface/[0.08]"
      );
    case "filled":
      return classNames(
        base,
        "rounded-full bg-sys-brand-primary px-6 py-2 text-label-large text-sys-brand-on-primary hover:shadow-elevation-1"
      );
    case "outlined":
      return classNames(
        base,
        "rounded-full border border-sys-brand-outline bg-transparent px-6 py-2 text-label-large text-sys-brand-primary"
      );
    default:
      return classNames(base, "bg-transparent p-0");
  }
}

function getCopyIconClassName(variant?: ButtonVariant) {
  if (variant === "filled") {
    return "pointer-events-none text-sys-brand-on-primary";
  }

  return "pointer-events-none text-sys-brand-on-primary-container";
}

export { copyTextToClipboard };

export default function CopyButton({
  textToCopy,
  copyFieldLabel,
  copyFieldId,
  ariaLabel,
  className = "",
  children,
  isMobile = false,
  feedback,
  successMessage,
  variant,
  showCopyIcon,
  iconPosition = "start",
  onCopied,
  testId
}: CopyButtonProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current != null) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const resolvedAriaLabel = buildCopyAriaLabel({ ariaLabel, copyFieldLabel, copyFieldId });
  const analyticsAttributes = getCopyAnalyticsAttributes({ copyFieldLabel, copyFieldId });
  const resolvedFeedback = feedback ?? (onCopied ? "none" : "tooltip");
  const resolvedShowCopyIcon = showCopyIcon ?? !(variant && children);

  const handleCopy = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await copyTextToClipboard(textToCopy);
    } catch {
      showErrorSnackbar("Failed to copy");
      return;
    }

    onCopied?.();

    if (resolvedFeedback === "none") {
      return;
    }

    if (resolvedFeedback === "snackbar") {
      showSnackbar(successMessage ?? "Copied");
      return;
    }

    if (tooltipTimeoutRef.current != null) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setShowTooltip(true);
    tooltipTimeoutRef.current = setTimeout(() => {
      tooltipTimeoutRef.current = null;
      setShowTooltip(false);
    }, 2000);
  };

  return (
    <div className={`relative flex items-center gap-1 ${className}`}>
      {showTooltip && (
        <div className="absolute bottom-6 bg-sys-brand-inverse-surface text-sys-brand-inverse-on-surface px-2 py-1 rounded whitespace-nowrap">
          Copied
        </div>
      )}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={resolvedAriaLabel}
        data-testid={testId}
        {...analyticsAttributes}
        className={getCopyButtonClassName(variant)}
      >
        {iconPosition !== "end" && resolvedShowCopyIcon && (
          <Icon name="content_copy" size={isMobile ? 16 : 20} className={getCopyIconClassName(variant)} />
        )}
        {children}
        {iconPosition === "end" && resolvedShowCopyIcon && (
          <Icon name="content_copy" size={isMobile ? 16 : 20} className={getCopyIconClassName(variant)} />
        )}
      </button>
    </div>
  );
}
